import {
  NewsletterSection_Content,
  NewsletterSection as NewsletterSectionType,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  Image,
  RADIUS,
  SPACING,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {getAdjustedCdnUrl} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';
import {MissionBadge} from '../../../features/Coins/MissionBadge/MissionBadge';
import {
  NewsletterFormOpener,
  NewsletterFormOpenerProps,
} from './Components/NewsletterFormOpener';
import {useNewsletterTracking} from './hooks/useNewsletterTracking';

interface NewsletterSectionProps extends NewsletterSectionType {
  NewsletterLayoutComponent: NewsletterFormOpenerProps['LayoutComponent'];
  className?: string;
}

export const NewsletterSection: React.FC<NewsletterSectionProps> = ({
  type: newsletterFormType,
  NewsletterLayoutComponent,
  className,
}) => {
  const {ref} = useNewsletterTracking();

  if (!newsletterFormType) {
    return null;
  }

  const {
    content: {headline, subtitle, type: newsletterUserProcessState, badges},
    backgroundImage,
    isCreativeCampaign,
  } = getNewsletterContent(newsletterFormType);

  const missionRewardAmount = getMissionRewardAmount(
    newsletterUserProcessState,
  );

  const shouldHideSubline =
    !subtitle ||
    ((newsletterUserProcessState?.$case === 'notSubscribed' ||
      newsletterUserProcessState?.$case === 'pending') &&
      missionRewardAmount);

  const dataTestid = 'newsletterFormSection';

  return (
    <Spacer
      data-testid={dataTestid}
      data-is-creative-campaign={isCreativeCampaign}
      ref={ref}
      className={className}
    >
      <Wrapper
        backgroundImage={backgroundImage ? backgroundImage.url : undefined}
      >
        <InfoWrapper>
          <Headline data-testid={`${dataTestid}Headline`}>{headline}</Headline>
          {!shouldHideSubline && <Subtitle>{subtitle}</Subtitle>}
          {missionRewardAmount && (
            <StyledMissionBadge
              small
              showCoinsLabel
              reward={missionRewardAmount}
            />
          )}
          <Badges data-testid={`${dataTestid}Badges`}>
            {badges.map(badge => (
              <Badge data-testid={`${dataTestid}Badge`} key={badge.label}>
                <BadgeContent>
                  <BadgeIcon
                    src={getAdjustedCdnUrl(badge.icon.url, {
                      bg: 'transparent',
                    })}
                    breakpoints={[]}
                    widths={[24, 40, 60]}
                    ratio="1:1"
                    disableFixedRatio
                    alt={badge.icon.description}
                  />
                  <BadgeText>{badge.label}</BadgeText>
                </BadgeContent>
              </Badge>
            ))}
          </Badges>
        </InfoWrapper>

        <NewsletterFormOpener
          newsletterUserProcessState={newsletterUserProcessState}
          isCreativeCampaign={isCreativeCampaign}
          LayoutComponent={NewsletterLayoutComponent}
        />
      </Wrapper>
    </Spacer>
  );
};

const getNewsletterContent = (
  newsletterFormType: NonNullable<NewsletterSectionType['type']>,
) => {
  if (newsletterFormType.$case === 'creativeCampaign') {
    return {
      ...newsletterFormType.creativeCampaign,
      backgroundImage: undefined,
      isCreativeCampaign: true,
    };
  }

  return {
    ...newsletterFormType.newsletter,
    isCreativeCampaign: false,
  };
};

const getMissionRewardAmount = (
  newsletterUserProcessState: NewsletterSection_Content['type'],
) => {
  switch (newsletterUserProcessState?.$case) {
    case 'pending':
      return newsletterUserProcessState.pending.rewardAmount;
    case 'notSubscribed':
      return newsletterUserProcessState.notSubscribed.rewardAmount;
    default:
      return undefined;
  }
};

const Spacer = styled.div`
  background-color: ${COLORS.White};
  padding: 0 ${SPACING['X.Lg']} ${SPACING['Y.Lg']};

  ${tabletAndUpLinaria(`
    padding: ${SPACING['Y.Xl3']} 0 ${SPACING['Y.Lg']};
  `)}
`;

const Wrapper = styled.div<{
  backgroundImage?: string;
}>`
  background-image: ${props => `url(${props.backgroundImage})`};
  background-size: cover;
  border-radius: ${RADIUS.Md};
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;

  [data-is-creative-campaign='true'] & {
    background-image: initial;
    flex-direction: column;
    margin: auto;
    max-width: 600px;
    padding: 0;
    text-align: center;
    width: 100%;
  }

  ${tabletAndUpLinaria(`
    height: 100%;
    justify-content: space-between;

    &[data-is-creative-campaign='true'] {
      gap: 16px;
      margin: 16px auto;
      padding: 0;
    }
  `)}
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['X.Xs']};

  ${tabletAndUpLinaria(`
    max-width: 350px;

    [data-is-creative-campaign='true'] & {
      gap: ${SPACING['Y.Lg']};
      max-width: initial;
    }
  `)}
`;

const Headline = styled(Text)`
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.44;
  width: 72%;

  [data-is-creative-campaign='true'] & {
    width: 100%;
    color: ${COLORS.Black};
  }

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px24};
    line-height: 1.16;
    width: 60%;

    [data-is-creative-campaign='true'] & {
      width: 100%;
      font-size: ${FONT_SIZES.px18};
      line-height: 1.44;
    }
  `)}
`;

const Subtitle = styled(Text)`
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.5;
  width: 72%;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px14};
    line-height: 1.42;
    width: 60%;
  `)}
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  [data-is-creative-campaign='true'] & {
    justify-content: center;
  }

  ${tabletAndUpLinaria(`
    gap: 12px;

    [data-is-creative-campaign='true'] & {
      margin-bottom: 16px;
    }
  `)}
`;

const Badge = styled.div`
  display: flex;
  font-size: ${FONT_SIZES.px16};
  margin: 0;
`;

const BadgeContent = styled.div`
  align-items: center;
  display: flex;
  background-color: ${COLORS['Fill.Grey.Primary']};
  gap: ${SPACING['Y.Sm']};
  border-radius: ${RADIUS.Round};
  padding: ${SPACING['Y.Sm']} 8px;

  ${tabletAndUpLinaria(`
    padding: 4px 10px 4px 8px;
  `)}
`;

const BadgeIcon = styled(Image)`
  height: auto;
  width: 20px;
`;

const BadgeText = styled(Text)`
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.5;

  [data-is-creative-campaign='true'] & {
    color: ${COLORS['Text.Black']};
    font-weight: ${FONT_WEIGHTS.mediumBold};
  }
`;

const StyledMissionBadge = styled(MissionBadge)`
  margin-block: ${SPACING['Y.Md']};
  background-color: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(4px);

  > span {
    color: ${COLORS['Text.White']};
  }

  > svg {
    fill: ${COLORS['Fill.White']};
  }
`;
